import React from 'react';
import PropTypes from 'prop-types';
import { Draggable } from '@hello-pangea/dnd';
import StatusBadge from './StatusBadge';
import TestCaseForm from './TestCaseForm';
import { HiOutlinePencil, HiOutlineTrash, HiOutlineSparkles, HiOutlineClock, HiOutlineRefresh, HiOutlineMenu, HiOutlineTag } from 'react-icons/hi';
import { FiMessageSquare } from 'react-icons/fi';

export default function TestCaseRow({
  tc, globalIndex, req, projectId, canEdit, editingTC,
  getEnvStatut, getEnvCommentaire, selectedEnv,
  loadProject, handleUpdateTC, setEditingTC, setConfirmAction,
  setAiModal, setAuditModal, setCommentThread, setObservationModal,
  handleAssign, usersList, projectTags, setTagTCModal,
  syncingItemId, handleSyncTestCase, filteredTCsLength, filterMyTC,
}) {
  const isDragDisabled = !!editingTC || filterMyTC || filteredTCsLength > 20;

  return (
    <Draggable key={tc.id} draggableId={`tc-${tc.id}`} index={globalIndex} isDragDisabled={isDragDisabled}>
      {(tcDragProvided, tcDragSnapshot) => (
        editingTC?.id === tc.id ? (
          <tr ref={tcDragProvided.innerRef} {...tcDragProvided.draggableProps} {...tcDragProvided.dragHandleProps}>
            <td colSpan="10"><TestCaseForm initial={editingTC} onSubmit={handleUpdateTC} onCancel={() => setEditingTC(null)} /></td>
          </tr>
        ) : (
          <tr id={`tc-${tc.id}`} ref={tcDragProvided.innerRef} {...tcDragProvided.draggableProps} className={tcDragSnapshot.isDragging ? 'dragging' : ''}>
            <td {...tcDragProvided.dragHandleProps} className="drag-handle" title="Glisser pour reordonner">
              <HiOutlineMenu style={{ color: 'var(--gray-400)' }} />
            </td>
            <td><span className="req-id" style={{ fontSize: '0.65rem' }}>{tc.ct_id}</span></td>
            <td className="small">{tc.cas_test}</td>
            <td className="small">{tc.prerequis}</td>
            <td className="small action-text">{tc.action}</td>
            <td className="small">{tc.resultat_attendu}</td>
            <td>
              <div className="d-flex align-items-center gap-1">
                <StatusBadge projectId={projectId} testCaseId={tc.id} statut={getEnvStatut(tc)} commentaire={getEnvCommentaire(tc)} attachments={tc.attachments || []} onUpdate={loadProject} environment={selectedEnv} />
                {['KO', 'BL'].includes(getEnvStatut(tc)) && (getEnvCommentaire(tc) || (tc.attachments && tc.attachments.length > 0)) && (
                  <button
                    className="comment-indicator-btn"
                    onClick={() => setObservationModal({ testCase: tc, commentaire: getEnvCommentaire(tc) })}
                    title="Voir l'observation et les captures"
                  >
                    <FiMessageSquare />
                    {tc.attachments && tc.attachments.length > 0 && (
                      <span style={{ fontSize: '0.55rem', verticalAlign: 'super', color: 'var(--danger)' }}>{tc.attachments.length}</span>
                    )}
                  </button>
                )}
              </div>
            </td>
            <td>
              <div className="d-flex flex-wrap gap-1 align-items-center">
                {(tc.tags || []).map(t => (
                  <span key={t.id} className="tag-chip" style={{ '--tag-color': t.color }}>{t.name}</span>
                ))}
                {canEdit && projectTags.length > 0 && (
                  <button className="tag-add-btn" onClick={() => setTagTCModal({ tcId: tc.id, currentTagIds: (tc.tags || []).map(t => t.id) })} title="Gerer les tags">
                    <HiOutlineTag />
                  </button>
                )}
              </div>
            </td>
            <td>
              {canEdit ? (
                <select
                  className="form-select form-select-sm"
                  style={{ fontSize: '0.72rem', padding: '2px 4px' }}
                  value={tc.assigned_to_id || ''}
                  onChange={(e) => handleAssign(tc.id, e.target.value ? Number.parseInt(e.target.value) : null)}
                >
                  <option value="">--</option>
                  {usersList.map((u) => (
                    <option key={u.id} value={u.id}>{u.username}</option>
                  ))}
                </select>
              ) : (
                <span className="small text-muted">{tc.assigned_to_username || '--'}</span>
              )}
            </td>
            <td>
              <div className="d-flex gap-1">
                {canEdit && <button className="btn btn-ghost btn-sm" onClick={() => setEditingTC(tc)} title="Modifier"><HiOutlinePencil /></button>}
                {canEdit && <button className="btn btn-ghost btn-sm" onClick={() => setConfirmAction({ type: 'tc', id: tc.id })} title="Supprimer" style={{ color: 'var(--danger)' }}><HiOutlineTrash /></button>}
                {canEdit && <button className="btn btn-ghost btn-sm" onClick={() => setAiModal({ mode: 'improve', requirement: req, testCase: tc })} title="Ameliorer avec l'IA" style={{ color: 'var(--warning)' }}><HiOutlineSparkles /></button>}
                <button className="btn btn-ghost btn-sm" onClick={() => setAuditModal({ entityType: 'test_case', entityId: tc.id, entityLabel: tc.ct_id })} title="Historique"><HiOutlineClock /></button>
                <button className="btn btn-ghost btn-sm" onClick={() => setCommentThread({ testCase: tc, projectId })} title="Discussion" style={{ color: 'var(--info)', position: 'relative' }}>
                  <FiMessageSquare />
                  {tc.comments_count > 0 && <span className="comment-badge" style={{ position: 'absolute', top: -2, right: -4 }}>{tc.comments_count}</span>}
                </button>
                {canEdit && (
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={!!syncingItemId}
                    onClick={() => handleSyncTestCase(tc.id, tc.ct_id)}
                    title="Sync vers Squash TM"
                    style={{ color: 'var(--secondary)' }}
                  >
                    {syncingItemId === `tc-${tc.id}` ? <span className="spinner-border spinner-border-sm"></span> : <HiOutlineRefresh />}
                  </button>
                )}
              </div>
            </td>
          </tr>
        )
      )}
    </Draggable>
  );
}

TestCaseRow.propTypes = {
  tc: PropTypes.object.isRequired,
  globalIndex: PropTypes.number.isRequired,
  req: PropTypes.object.isRequired,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  canEdit: PropTypes.bool.isRequired,
  editingTC: PropTypes.object,
  getEnvStatut: PropTypes.func.isRequired,
  getEnvCommentaire: PropTypes.func.isRequired,
  selectedEnv: PropTypes.string.isRequired,
  loadProject: PropTypes.func.isRequired,
  handleUpdateTC: PropTypes.func.isRequired,
  setEditingTC: PropTypes.func.isRequired,
  setConfirmAction: PropTypes.func.isRequired,
  setAiModal: PropTypes.func.isRequired,
  setAuditModal: PropTypes.func.isRequired,
  setCommentThread: PropTypes.func.isRequired,
  setObservationModal: PropTypes.func.isRequired,
  handleAssign: PropTypes.func.isRequired,
  usersList: PropTypes.array.isRequired,
  projectTags: PropTypes.array.isRequired,
  setTagTCModal: PropTypes.func.isRequired,
  syncingItemId: PropTypes.string,
  handleSyncTestCase: PropTypes.func.isRequired,
  filteredTCsLength: PropTypes.number.isRequired,
  filterMyTC: PropTypes.bool.isRequired,
};
